import { notify } from '../../../app';
import { useFetch, usePermissionAllowed } from '../../../hooks';
import { useInterval } from '../../../hooks';
import { PERMISSIONS } from '../../../shared/constants/permissions';
import {
  getConversationAudio,
  getConversationParticipantInfo,
  getConversationSelfCostStatistics,
} from '../../../api';
import { getConversation, getConversationMessages } from '../../../api';
import { getShortAgentsMap } from '../../billing/utils/getAgentsShortMap';

const REFRESH_MESSAGES_INTERVAL = 10 * 1000;
const REFRESH_CONVO_INTERVAL = 1 * 60 * 1000;

export const useConversation = (id: string) => {
  const {
    data: conversation,
    intializing: loadingConvo,
    refresh: refreshConvo,
  } = useFetch(() => getConversation(id), [id]);
  const {
    data: audio,
    intializing: loadingAudio,
    refresh: refreshAudio,
  } = useFetch(() => getConversationAudio(id), [id]);
  useInterval(refreshConvo, REFRESH_CONVO_INTERVAL, !conversation?.ended_at);

  const {
    data: messages,
    intializing: intializinggMessages,
    loading: loadingMessages,
    refresh: refreshMessages,
  } = useFetch(() => getConversationMessages(id), [id]);

  const {
    data: messagesEnglish,
    intializing: intializinggMessagesEnglish,
    loading: loadingMessagesEnglish,
  } = useFetch(() => getConversationMessages(id, 'EN'), [id]);

  useInterval(
    refreshMessages,
    REFRESH_MESSAGES_INTERVAL,
    !conversation?.ended_at
  );

  const canViewSelfCost = usePermissionAllowed(PERMISSIONS.VIEW_SELF_COST_DATA);

  const {
    data: selfCostStatistics,
    intializing: intializinggSelfCostStatistics,
    loading: loadingSelfCostStatistics,
    refresh: refreshSelfCostStatistics,
  } = useFetch(() => getConversationSelfCostStatistics(id), [id], {
    skip: !canViewSelfCost,
  });

  useInterval(
    refreshSelfCostStatistics,
    REFRESH_MESSAGES_INTERVAL,
    !conversation?.ended_at
  );

  const participant_external_id =
    conversation?.participant_external_id ||
    // TODO: remove this line
    messages?.find(m => m.author_type === 'user')?.author_identifier;

  const { data: participant, intializing: loadingParticipant } = useFetch(
    () =>
      participant_external_id
        ? getConversationParticipantInfo(participant_external_id)
        : null,
    [participant_external_id],
    {
      skip: !participant_external_id,
      onError: (ex: any) => {
        if (ex?.status === 404) {
          return notify.error('Participant info not found');
        }

        notify.error('Something went wrong');
      },
    }
  );

  const { loading, data: title } = useFetch(async () => {
    if (!conversation?.agent_id) return;
    const data = await getShortAgentsMap();

    return data?.[conversation?.agent_id as string];
  }, [conversation?.agent_id]);

  return {
    messages,
    participant,
    conversation,
    audio,
    loadingAudio,
    refreshAudio,
    title,
    loadingTitle: loading,
    loading:
      loadingConvo ||
      loadingMessages ||
      (loadingParticipant && !!participant_external_id),
    loadingMessages: intializinggMessages || loadingMessages,
    messagesEnglish,
    loadingMessagesEnglish:
      intializinggMessagesEnglish || loadingMessagesEnglish,
    selfCostStatistics,
    loadingCostStatistics:
      intializinggSelfCostStatistics || loadingSelfCostStatistics,
    loadingSelfCostStatistics,
  };
};

export type { MessageResponse } from '../../../api';
